/**
 * Module dependencies.
 */

import React, { useCallback, useRef, useState } from 'react';
import { StyledButton } from './notes.styles';
import { FolderPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'app/components/ui/dialog';
import { Button } from 'app/components/atoms/button/button';
import { useForm } from 'react-hook-form';
import { InputField } from 'app/components/atoms/forms/fields/input/input-field';
import { requiredRule } from 'app/core/utils/field-rules';
import { useActionsContent } from './actions-provider';

/**
 * `Props` type.
 */

type Props = {
  trigger?: (open: () => void) => React.ReactNode;
};

/**
 * Export `AddFolder` component.
 */

export function AddFolder({ trigger }: Props = {}): JSX.Element {
  const [translate] = useTranslation();
  const { postNewItem } = useActionsContent();
  const formRef = useRef<HTMLFormElement>(null);
  const [open, setOpen] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = useForm({
    mode: 'all'
  });

  const handleCancel = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOk = useCallback(() => {
    formRef.current?.requestSubmit();
  }, []);

  const onSubmit = useCallback(
    async (data: any) => {
      try {
        await postNewItem({
          name: data.name,
          type: 'folder'
        });

        reset();
        setOpen(false);
      } catch (error) {
        console.error('Error creating folder:', error);
      }
    },
    [postNewItem, reset]
  );

  return (
    <>
      {trigger ? (
        trigger(() => setOpen(true))
      ) : (
        <StyledButton onClick={() => setOpen(true)}>
          <FolderPlus />

          {translate('notes.actions.newFolder')}
        </StyledButton>
      )}

      <Dialog onOpenChange={setOpen} open={open}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{translate('notes.actions.newFolder')}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
            <InputField
              control={control}
              label={translate('common.labels.name')}
              name={'name'}
              rules={requiredRule(translate)}
            />
          </form>

          <DialogFooter>
            <Button onClick={handleCancel}>{translate('common.actions.cancel')}</Button>

            <Button loading={isSubmitting} onClick={handleOk} type={'primary'}>
              {translate('common.actions.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
